import { PartThumbnail as SharedPartThumbnail } from '../../../components/PartThumbnail'
import { cx } from '../../../components/ui/cx'

export interface StagingPartThumbnailProps {
  /** `MatchedPart.thumbnailUrl`/`IdentifiedPart.thumbnailUrl` — the real,
   * resolved thumbnail URL, or null/undefined on a miss. */
  readonly url: string | null | undefined
  /** The call site's own box-sizing class (`staging-row-thumb`) — applied to
   * BOTH the real `<img>` and the placeholder, so a resolved image never
   * shifts the row layout. */
  readonly className?: string
}

/**
 * UID-517 item A — the staging BOM parts table's real-thumbnail-or-
 * placeholder box.
 *
 * Reuses the SHARED `PartThumbnail` real-image/onError-fallback machinery
 * (`components/PartThumbnail.tsx`) with the SAME decorative glyph
 * placeholder cart/RFQ lines already fall back to
 * (`.part-thumbnail-placeholder-glyph`, `styles/ui.css`), replacing the
 * plain empty `.staging-row-thumb-placeholder` box this row used to render
 * (no icon at all) — same rationale as the RFQ/workspace swap this ticket
 * already made off `CatalogResultThumb`.
 *
 * W5 — keyed on `url` itself, per `SharedPartThumbnail`'s own doc comment
 * ("callers should key this component on the url itself when it can change
 * across a refetch"): its load-failure flag is local/permanent per instance,
 * so a leaf whose url changes across a re-read (a cross-session match
 * resolving, a demo backfill running) must not keep rendering a stale 404's
 * placeholder.
 */
export function PartThumbnail({ url, className }: StagingPartThumbnailProps) {
  return (
    <SharedPartThumbnail
      key={url ?? 'placeholder'}
      url={url}
      imgClassName={cx(className)}
      placeholder={<StagingThumbnailPlaceholder className={className} />}
    />
  )
}

function StagingThumbnailPlaceholder({ className }: { readonly className?: string | undefined }) {
  return (
    <span
      className={cx(className, 'staging-row-thumb-placeholder', 'part-thumbnail-placeholder-glyph')}
      aria-hidden="true"
    >
      {/* Isometric 3D box glyph (Lucide "box") — same glyph the cart/rfq
          placeholder uses, so a part's decorative "no image" tile reads
          identically across every surface this ticket touches. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    </span>
  )
}
